import { db, storage } from "../firebase";
import {
  collection,
  query,
  where,
  orderBy,
  limit,
  onSnapshot,
  doc,
  getDoc,
  getDocs,
  updateDoc,
  deleteDoc,
  increment,
  serverTimestamp,
} from "firebase/firestore";
import { ref, deleteObject } from "firebase/storage";

// Mirrors src/features/reels on the app side. The portal talks to the same
// propertyReels documents, so the write shapes here must stay in step with
// reelsRepository.ts — and with the security rules, which only let an owner
// touch caption/status and only let anyone else bump counters.

export const REELS_COLLECTION = "propertyReels";
export const MAX_CAPTION_LENGTH = 200;
/** Comments render in one pass; the app caps its listener at the same number. */
const COMMENTS_LIMIT = 300;
/** Safety bound on the delete-drain loop below: 300 x 20 = 6000 comments. */
const MAX_COMMENT_PAGES = 20;

export function reelDoc(reelId) {
  return doc(db, REELS_COLLECTION, reelId);
}

export function reelCommentsCol(reelId) {
  return collection(db, REELS_COLLECTION, reelId, "comments");
}

// ─── Feed / listing ─────────────────────────────────────────────────────────

/**
 * Live subscription to reels — every reel for admins, one dealer's reels when
 * `dealerId` is passed.
 *
 * Sorting happens client-side on purpose. Firestore's orderBy silently drops
 * documents that lack the ordered field, so a reel whose `createdAt` server
 * timestamp is still resolving would vanish from the console (the same trap
 * PropertiesPanel documents). Client-side sort also keeps the dealer query on
 * a single-field index instead of needing a composite one.
 */
export function subscribeReels({ dealerId } = {}, onData, onError) {
  const q = dealerId
    ? query(collection(db, REELS_COLLECTION), where("dealerId", "==", dealerId))
    : query(collection(db, REELS_COLLECTION));

  return onSnapshot(
    q,
    (snap) => {
      const list = [];
      snap.forEach((d) => list.push({ id: d.id, ...d.data() }));
      list.sort((a, b) => toMillis(b.createdAt) - toMillis(a.createdAt));
      onData(list);
    },
    (err) => {
      console.error("Reels subscription failed:", err);
      onError?.(err);
    }
  );
}

// ─── Mutations ──────────────────────────────────────────────────────────────

export class ReelPropertyUnavailableError extends Error {
  constructor() {
    super("The listing for this Reel is no longer available.");
    this.name = "ReelPropertyUnavailableError";
  }
}

/**
 * Publishes or hides a reel. Republishing is gated on the linked listing still
 * being alive — deleting a listing hides its reels, and putting one back in the
 * feed pointing at a tombstoned listing would strand every viewer who taps it.
 */
export async function setReelStatus(reel, status) {
  if (status === "active" && reel.propertyId) {
    const snap = await getDoc(doc(db, "properties", reel.propertyId));
    const property = snap.exists() ? snap.data() : null;
    if (!property || property.status === "deleted") {
      throw new ReelPropertyUnavailableError();
    }
  }
  await updateDoc(reelDoc(reel.id), { status, updatedAt: serverTimestamp() });
}

export async function updateReelCaption(reelId, caption) {
  await updateDoc(reelDoc(reelId), {
    caption: caption.slice(0, MAX_CAPTION_LENGTH),
    updatedAt: serverTimestamp(),
  });
}

/**
 * Removes a reel and everything hanging off it: comments first, then the
 * document, then the media.
 *
 * Comments have to go before the parent document. The rule that lets a dealer
 * moderate their own reel resolves the owner with
 * get(propertyReels/$(reelId)).data.dealerId — once the parent is gone that
 * lookup has nothing to read and every remaining comment becomes undeletable,
 * orphaned under a document that no longer exists. Admins are not subject to
 * that ordering, but there is no reason to have two delete paths.
 *
 * Storage removal is best-effort: a missing file should not fail a delete the
 * user already confirmed.
 */
export async function deleteReel(reel) {
  // Drain in pages rather than one capped read, so a reel that outgrew the
  // listener's limit does not leave comments behind. Bounded so a permission
  // error on every document cannot spin forever.
  for (let page = 0; page < MAX_COMMENT_PAGES; page += 1) {
    const snap = await getDocs(query(reelCommentsCol(reel.id), limit(COMMENTS_LIMIT)));
    if (snap.empty) break;
    const results = await Promise.all(
      snap.docs.map((c) =>
        deleteDoc(c.ref).then(
          () => true,
          (err) => {
            console.warn("Could not remove reel comment", c.id, err);
            return false;
          }
        )
      )
    );
    if (!results.some(Boolean)) break; // nothing removable left
  }

  await deleteDoc(reelDoc(reel.id));

  for (const path of [reel.videoPath, reel.thumbPath]) {
    if (!path) continue;
    try {
      await deleteObject(ref(storage, path));
    } catch (err) {
      // Already gone or unreachable — the document is what mattered.
      console.warn("Could not remove reel media", path, err);
    }
  }
}

// ─── Comments ───────────────────────────────────────────────────────────────

export function subscribeReelComments(reelId, onData, onError) {
  const q = query(reelCommentsCol(reelId), orderBy("createdAt", "asc"), limit(COMMENTS_LIMIT));
  return onSnapshot(
    q,
    (snap) => {
      const list = [];
      snap.forEach((d) => list.push({ id: d.id, ...d.data() }));
      onData(list);
    },
    (err) => {
      console.error("Reel comments subscription failed:", err);
      onError?.(err);
    }
  );
}

/**
 * Deletes a comment and, for a top-level one, its replies. The stored
 * commentsCount is decremented by what actually went away, matching the app's
 * behaviour and staying inside the rules' ±1-per-write counter limit.
 */
export async function deleteReelComment(reelId, commentId, replyIds = []) {
  await deleteDoc(doc(reelCommentsCol(reelId), commentId));
  let removed = 1;
  for (const replyId of replyIds) {
    try {
      await deleteDoc(doc(reelCommentsCol(reelId), replyId));
      removed += 1;
    } catch (err) {
      console.warn("Could not remove reply", replyId, err);
    }
  }
  // One write per removed document: the counter rules allow a delta of at most
  // 1 per update, so a thread of five cannot be decremented in a single call.
  for (let i = 0; i < removed; i += 1) {
    // eslint-disable-next-line no-await-in-loop
    await updateDoc(reelDoc(reelId), { commentsCount: increment(-1) }).catch(() => {});
  }
}

/** Flat comment docs → [{ comment, replies }] in Instagram's single-level shape. */
export function threadComments(comments) {
  const tops = comments.filter((c) => !c.parentId);
  const repliesByParent = new Map();
  comments
    .filter((c) => c.parentId)
    .forEach((c) => {
      const bucket = repliesByParent.get(c.parentId) || [];
      bucket.push(c);
      repliesByParent.set(c.parentId, bucket);
    });
  return tops.map((comment) => ({
    comment,
    replies: repliesByParent.get(comment.id) || [],
  }));
}

// ─── Formatting ─────────────────────────────────────────────────────────────

function toMillis(timestamp) {
  if (!timestamp) return 0;
  if (typeof timestamp.toMillis === "function") return timestamp.toMillis();
  if (timestamp.seconds) return timestamp.seconds * 1000;
  return 0;
}

/** 1234 → "1.2K", 2000000 → "2M". Clamped at zero: an unsave can dip a stored counter negative. */
export function formatCount(count) {
  const n = Math.max(0, count || 0);
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(n % 1_000_000 === 0 ? 0 : 1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(n % 1_000 === 0 ? 0 : 1)}K`;
  return String(n);
}

export function formatReelDate(timestamp) {
  const millis = toMillis(timestamp);
  if (!millis) return "";
  return new Date(millis).toLocaleDateString([], {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

export function formatReelDateTime(timestamp) {
  const millis = toMillis(timestamp);
  if (!millis) return "";
  return new Date(millis).toLocaleString([], {
    day: "numeric",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export { toMillis as reelTimestampMillis };
